import React, { useMemo, useState } from 'react';
import { getStyle } from '../ui';
import { useTranslation } from 'react-i18next';
import useTheme from '@material-ui/core/styles/useTheme';
import styled, { ThemeProvider } from 'styled-components';
import cn from 'classnames';
import tw from 'twin.macro';
import MultiSelectCheckbox from '../ui/inputs/MultiSelectCheckbox/MultiSelectCheckbox';
import { FilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import { getTableColumnName } from '../../helpers/translations';
import { TableColumnId } from '../../store/table/types';
import { GridColumnProps } from '@progress/kendo-react-grid';
import moment from 'moment';

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  variant?: 'text' | 'outlined' | 'solid';
  roundedEdges?: 'rounded-none' | 'rounded' | 'rounded-full';
  ref?: any;
};

const Button = ({
  disabled,
  className,
  icon,
  iconPosition = 'left',
  variant = 'solid',
  roundedEdges = 'rounded-full',
  children,
  ...buttonProps
}: ButtonProps): JSX.Element => {
  const theme = useTheme();
  return (
    <ThemeProvider theme={theme}>
      <StyledButton
        className={cn(className, variant, roundedEdges, {
          disabled,
          'right-icon': iconPosition == 'right',
        })}
        {...buttonProps}
      >
        {icon && <span className="-mx-2">{icon}</span>}
        <span>{children}</span>
      </StyledButton>
    </ThemeProvider>
  );
};

const StyledButton = styled.button`
  ${tw`
    inline-flex items-center justify-center whitespace-nowrap px-2 text-sm`}

  border-color: ${(props) => props.theme.palette.primary.main};

  &.disabled {
    ${tw`pointer-events-none opacity-20`}
  }

  &.right-icon {
    ${tw`flex-row-reverse`}
  }
  &.solid {
    ${tw`font-semibold border-2`}
    color: #ffffff;
    background-color: ${(props) => props.theme.palette.primary.main};
  }
`;

interface Props {
  filters: FilterDescriptor[];
  gridColumnProps: GridColumnProps[];
  onClearAll: () => void;
  onSave: (filters: FilterDescriptor[]) => void;
}

export const FiltersManager: React.FC<Props> = ({ filters, gridColumnProps, onClearAll, onSave }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const columnByField = useMemo(() => {
    const byField: { [key: string]: GridColumnProps } = {};
    gridColumnProps.forEach((columnProp) => {
      if (columnProp.field) {
        byField[columnProp.field] = columnProp;
      }
    });
    return byField;
  }, []);

  const parseId = (field: string, value: null | string | { id: string; label: string }): string => {
    if (value === null || typeof value !== 'object') {
      return field;
    } else {
      return `${field}#${value.id}`;
    }
  };

  const filterToOptions = (filter: FilterDescriptor): { id: string; label: string }[] => {
    let parsed: { id: string; label: string }[];
    let id, label;
    const field = filter.field as string;
    const column = columnByField[field];
    if (column) {
      const fieldId = column.id as TableColumnId;
      const colName = getTableColumnName(fieldId, t) ?? column.title;
      if (Array.isArray(filter.value)) {
        parsed = filter.value.map((value) => {
          const { label: fLabel } = value;
          id = parseId(field, value);
          label = `${colName}: ${fLabel}`;
          return { id, label };
        });
      } else {
        const value = column.filter == 'date' ? moment(filter.value as Date).format('l') : filter.value; // This outputs a format like "6/9/2025"
        id = parseId(field, value);
        label = `${colName}: ${value}`;
        parsed = [{ id, label }];
      }
    } else {
      parsed = [{ id: t('N/A'), label: t('N/A') }];
    }
    return parsed;
  };

  const options = filters.map((filter) => filterToOptions(filter)).flat();

  const [selection, setSelection] = useState<string[]>(options.map((option) => option.id));

  return (
    <div className="w-56">
      <div className="mb-3">
        {filters.length > 0 ? (
          <MultiSelectCheckbox
            className="flex flex-col space-y-2 text-sm"
            value={selection}
            options={options}
            onChange={setSelection}
          />
        ) : (
          <p>{t('No filters yet')}</p>
        )}
      </div>
      <hr />
      <div className="flex items-center w-lg justify-between mt-3">
        <div>
          <Button
            roundedEdges="rounded"
            disabled={!filters.length}
            onClick={() => {
              const newFilters: FilterDescriptor[] = [];
              filters.forEach((filter) => {
                const field = filter.field as string;
                if (Array.isArray(filter.value)) {
                  const filtered = filter.value.filter((value) => selection.includes(parseId(field, value)));
                  if (filtered.length > 0) {
                    newFilters.push({ ...filter, value: filtered });
                  }
                } else if (selection.includes(parseId(field, filter.value))) {
                  newFilters.push(filter);
                }
              });
              onSave(newFilters);
            }}
          >
            {t('Save')}
          </Button>
        </div>
        <div>
          <button
            style={{ ...getStyle(theme, 'text'), marginRight: '0.5rem' }}
            onClick={onClearAll}
            disabled={!filters.length}
            className={!filters.length ? 'pointer-events-none opacity-20' : ''}
          >
            {t('Clear all')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FiltersManager;
